import { isValidElement, type ReactNode } from 'react';
import {
  DEFAULT_TABLE_TEXT_ALIGN,
  POPOVER_COLUMN_FALLBACK,
  POPOVER_DEFAULT_WIDTH,
  POPOVER_EMPTY_VALUE,
  POPOVER_ESTIMATED_HEIGHT,
  POPOVER_MIN_WIDTH,
  POPOVER_ROW_BELOW_GAP,
  POPOVER_ROW_GAP,
  POPOVER_VIEWPORT_PADDING,
  TABLE_MARKDOWN_ALIGN_SEGMENT,
} from './Cmarkdowntable.constants';
import {
  TABLE_TEXT_ALIGN,
  type CmarkdowntableCell,
  type CmarkdowntablePopoverEntry,
  type CmarkdowntablePopoverPosition,
  type CmarkdowntablePopoverRect,
  type CmarkdowntableTextAlign,
} from './Cmarkdowntable.types';

interface CellAlignmentInput {
  cellAlign?: CmarkdowntableTextAlign | null;
  textAlign?: CmarkdowntableTextAlign;
  hebrew?: boolean;
  isNumeric?: boolean;
}

/** A cell whose text is genuinely numeric (figures, money, percents, ranges).
 *  Only these may keep an author-declared right alignment — models emit
 *  `---:` delimiter rows out of habit, and right-aligned prose read as an
 *  alignment bug (issue 4uu603x). */
const NUMERIC_CELL_TEXT_REGEX = /^[\s$€£¥%+±~-]*\d[\d\s.,:%/×xkKmMbB()+±~-]*$/;

function isNumericCellText(content: ReactNode): boolean {
  const text = normalizeText(extractTextFromNode(content));
  return text.length > 0 && NUMERIC_CELL_TEXT_REGEX.test(text);
}

interface TableCellRenderState {
  alignment: CmarkdowntableTextAlign;
  hebrew: boolean;
  isNumericColumn: boolean;
}

interface PopoverSize {
  width: number;
  height: number;
}

function resolveCellAlignment(input: CellAlignmentInput): CmarkdowntableTextAlign {
  // Author-declared right alignment is honoured only for numeric cells;
  // right-aligned prose reads as a bug, not a choice (issue 4uu603x).
  if (input.cellAlign === TABLE_TEXT_ALIGN.Right && !input.isNumeric) {
    return input.hebrew ? TABLE_TEXT_ALIGN.Right : DEFAULT_TABLE_TEXT_ALIGN;
  }
  if (input.cellAlign) return input.cellAlign;
  if (input.textAlign) return input.textAlign;
  if (input.hebrew) return TABLE_TEXT_ALIGN.Right;
  return DEFAULT_TABLE_TEXT_ALIGN;
}

export function resolveTableCellRenderState(input: {
  cell: CmarkdowntableCell;
  textAlign?: CmarkdowntableTextAlign;
}): TableCellRenderState {
  const hebrew = input.cell.hebrew === true;
  const alignment = resolveCellAlignment({
    cellAlign: input.cell.align,
    textAlign: input.textAlign,
    hebrew,
    isNumeric: isNumericCellText(input.cell.content),
  });

  return {
    alignment,
    hebrew,
    isNumericColumn: !hebrew && alignment === TABLE_TEXT_ALIGN.Right,
  };
}

export function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function extractTextFromNode(node: ReactNode): string {
  if (isEmptyTextNode(node)) {
    return '';
  }

  if (isPrimitiveTextNode(node)) {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node
      .map((item) => extractTextFromNode(item))
      .filter((item) => item.length > 0)
      .join(' ');
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return extractTextFromNode(node.props.children);
  }

  return '';
}

function isEmptyTextNode(node: ReactNode): boolean {
  return node == null || typeof node === 'boolean';
}

function isPrimitiveTextNode(node: ReactNode): node is string | number | bigint {
  return typeof node === 'string'
    || typeof node === 'number'
    || typeof node === 'bigint';
}

export function buildPopoverEntries(input: {
  headerCells: CmarkdowntableCell[];
  row: CmarkdowntableCell[];
}): CmarkdowntablePopoverEntry[] {
  const columnCount = Math.max(input.headerCells.length, input.row.length);

  return Array.from({ length: columnCount }, (_, columnIndex) => buildPopoverEntry({
    headerCells: input.headerCells,
    row: input.row,
    columnIndex,
  }));
}

function buildPopoverEntry(input: {
  headerCells: CmarkdowntableCell[];
  row: CmarkdowntableCell[];
  columnIndex: number;
}): CmarkdowntablePopoverEntry {
  const rawTitle = normalizeText(extractTextFromNode(input.headerCells[input.columnIndex]?.content));
  const rawValue = normalizeText(extractTextFromNode(input.row[input.columnIndex]?.content));

  return {
    title: withTextFallback(rawTitle, `${POPOVER_COLUMN_FALLBACK} ${input.columnIndex + 1}`),
    value: withTextFallback(rawValue, POPOVER_EMPTY_VALUE),
  };
}

function withTextFallback(value: string, fallback: string): string {
  return value || fallback;
}

function escapeCsvCell(value: string): string {
  if (/["\n\r,]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

export function tableToCsv(input: {
  headerCells: CmarkdowntableCell[];
  rows: CmarkdowntableCell[][];
}): string {
  const headerLine = input.headerCells
    .map((cell) => escapeCsvCell(normalizeText(extractTextFromNode(cell.content))))
    .join(',');
  const bodyLines = input.rows.map((row) => row
    .map((cell) => escapeCsvCell(normalizeText(extractTextFromNode(cell.content))))
    .join(','));

  return [headerLine, ...bodyLines].join('\n');
}

function escapeMarkdownCell(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\|/g, '\\|')
    .replace(/\r?\n/g, ' ');
}

function markdownAlignSegment(align: CmarkdowntableTextAlign): string {
  if (align === TABLE_TEXT_ALIGN.Center) return TABLE_MARKDOWN_ALIGN_SEGMENT.Center;
  if (align === TABLE_TEXT_ALIGN.Right) return TABLE_MARKDOWN_ALIGN_SEGMENT.Right;
  return TABLE_MARKDOWN_ALIGN_SEGMENT.Left;
}

export function tableToMarkdown(input: {
  headerCells: CmarkdowntableCell[];
  rows: CmarkdowntableCell[][];
  textAlign?: CmarkdowntableTextAlign;
}): string {
  const headerTexts = input.headerCells.map((cell) => escapeMarkdownCell(
    normalizeText(extractTextFromNode(cell.content)),
  ));
  const alignmentRow = input.headerCells.map((cell) => markdownAlignSegment(
    resolveCellAlignment({ cellAlign: cell.align, textAlign: input.textAlign }),
  ));
  const bodyRows = input.rows.map((row) => row.map((cell) => escapeMarkdownCell(
    normalizeText(extractTextFromNode(cell.content)),
  )));

  const formatRow = (cells: string[]): string => `| ${cells.join(' | ')} |`;
  return [
    formatRow(headerTexts),
    formatRow(alignmentRow),
    ...bodyRows.map((row) => formatRow(row)),
  ].join('\n');
}

export function clampNumber(input: { value: number; min: number; max: number }): number {
  return Math.min(Math.max(input.value, input.min), Math.max(input.min, input.max));
}

export function clampRectToViewport(input: {
  rect: CmarkdowntablePopoverRect;
  viewportWidth: number;
  viewportHeight: number;
}): CmarkdowntablePopoverRect {
  const width = Math.min(input.rect.width, input.viewportWidth - POPOVER_VIEWPORT_PADDING * 2);
  const height = Math.min(input.rect.height, input.viewportHeight - POPOVER_VIEWPORT_PADDING * 2);
  const left = clampNumber({
    value: input.rect.left,
    min: POPOVER_VIEWPORT_PADDING,
    max: input.viewportWidth - width - POPOVER_VIEWPORT_PADDING,
  });
  const top = clampNumber({
    value: input.rect.top,
    min: POPOVER_VIEWPORT_PADDING,
    max: input.viewportHeight - height - POPOVER_VIEWPORT_PADDING,
  });
  return { left, top, width, height };
}

export function resolveAnchoredPopoverPosition(input: {
  anchorRect: DOMRect;
  measuredWidth?: number;
  measuredHeight?: number;
  viewportWidth: number;
  viewportHeight: number;
}): CmarkdowntablePopoverPosition | null {
  if (isAnchorOutsideViewport(input)) {
    return null;
  }

  const popoverSize = resolvePopoverSize(input);
  const rowCenter = input.anchorRect.top + input.anchorRect.height / 2;
  const initialTop = rowCenter - popoverSize.height / 2;
  const unclampedPosition = resolveAnchoredPopoverLeft({
    anchorRect: input.anchorRect,
    initialTop,
    popoverWidth: popoverSize.width,
    viewportWidth: input.viewportWidth,
  });

  return {
    left: unclampedPosition.left,
    top: clampPopoverTop({
      top: unclampedPosition.top,
      popoverHeight: popoverSize.height,
      viewportHeight: input.viewportHeight,
    }),
    width: popoverSize.width,
  };
}

function isAnchorOutsideViewport(input: {
  anchorRect: DOMRect;
  viewportWidth: number;
  viewportHeight: number;
}): boolean {
  return input.anchorRect.bottom <= 0
    || input.anchorRect.top >= input.viewportHeight
    || input.anchorRect.right <= 0
    || input.anchorRect.left >= input.viewportWidth;
}

function resolvePopoverSize(input: {
  measuredWidth?: number;
  measuredHeight?: number;
  viewportWidth: number;
}): PopoverSize {
  return {
    width: isPositiveFiniteNumber(input.measuredWidth)
      ? input.measuredWidth
      : resolveDefaultPopoverWidth(input.viewportWidth),
    height: isPositiveFiniteNumber(input.measuredHeight)
      ? input.measuredHeight
      : POPOVER_ESTIMATED_HEIGHT,
  };
}

function isPositiveFiniteNumber(value?: number): value is number {
  return Boolean(value && Number.isFinite(value) && value > 0);
}

function resolveAnchoredPopoverLeft(input: {
  anchorRect: DOMRect;
  initialTop: number;
  popoverWidth: number;
  viewportWidth: number;
}): { left: number; top: number } {
  const rightAnchoredLeft = input.anchorRect.right + POPOVER_ROW_GAP;
  const left = rightAnchoredLeft + input.popoverWidth > input.viewportWidth - POPOVER_VIEWPORT_PADDING
    ? input.anchorRect.left - input.popoverWidth - POPOVER_ROW_GAP
    : rightAnchoredLeft;

  if (left >= POPOVER_VIEWPORT_PADDING) {
    return { left: Math.max(POPOVER_VIEWPORT_PADDING, left), top: input.initialTop };
  }

  return {
    left: Math.max(
      POPOVER_VIEWPORT_PADDING,
      Math.min(input.anchorRect.left, input.viewportWidth - input.popoverWidth - POPOVER_VIEWPORT_PADDING),
    ),
    top: input.anchorRect.bottom + POPOVER_ROW_BELOW_GAP,
  };
}

function clampPopoverTop(input: {
  top: number;
  popoverHeight: number;
  viewportHeight: number;
}): number {
  const minTop = POPOVER_VIEWPORT_PADDING;
  const maxTop = Math.max(minTop, input.viewportHeight - input.popoverHeight - POPOVER_VIEWPORT_PADDING);
  return Math.min(Math.max(input.top, minTop), maxTop);
}

export function downloadBlob(input: { blob: Blob; filename: string }): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const url = URL.createObjectURL(input.blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = input.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function shouldIgnoreRowActivation(target: EventTarget): boolean {
  return target instanceof HTMLElement && target.closest('a,button,input,textarea,select,label') != null;
}

function resolveDefaultPopoverWidth(viewportWidth: number): number {
  return Math.min(
    POPOVER_DEFAULT_WIDTH,
    Math.max(POPOVER_MIN_WIDTH, viewportWidth - POPOVER_VIEWPORT_PADDING * 2),
  );
}
